<template>
  <div class="tw:border tw:rounded-lg">
    <div class="tw:p-4 tw:md:flex tw:justify-between tw:items-center">
      <h4 class="tw:mb-0">{{ $t('History') }}</h4>
    </div>

    <LoadingOverlay :active="loading">
      <table class="tw:w-full">
        <thead>
          <tr class="tw:border-t tw:bg-zinc-50">
            <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">{{ $t('Description') }}</th>
            <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">{{ $t('Start date') }}</th>
            <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">{{ $t('End date') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="history in histories" :key="history.id" class="tw:border-t">
            <td class="tw:p-4">
              <div class="tw:flex tw:items-center tw:gap-3">
                <Icon :name="getHistoryIcon(history.type)" class="tw:text-zinc-500 tw:shrink-0" />
                <div>
                  <template v-if="history.type !== 'Allotment'">
                    <span v-html="$t('{historyType} for {userLink}', {historyType: getHistoryTypeLabel(history.type), userLink: `<a href='${history.resource.registration.links.show}'>${history.resource.registration?.firstname ?? ''} ${history.resource.registration?.lastname ?? ''}</a>`})"></span>
                  </template>
                  <template v-else>
                    {{ getHistoryTypeLabel(history.type) }}
                  </template>
                </div>
              </div>
            </td>
            <td class="tw:p-4 tw:text-sm tw:text-zinc-500">
              {{ dayjs(history.start_at).format('lll') }}
            </td>
            <td class="tw:p-4 tw:text-sm tw:text-zinc-500">
              <template v-if="history.end_at">
                {{ dayjs(history.end_at).format('lll') }}
              </template>
              <template v-else>
                <span class="tw:text-zinc-400">{{ $t('Ongoing') }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </LoadingOverlay>
  </div>
</template>

<script setup lang="ts">
import HistoryModel from "../../models/History";
import dayjs from "dayjs";
import { ref } from "vue";
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue";
import { useI18n } from "vue-i18n";
import type { History } from "@/utils/history";
import Icon from "@/components/ui/icon/Icon.vue";

// Props
interface Props {
  equipmentId: number | string;
}

// Extended History interface for component-specific properties
interface ExtendedHistory extends History {
  end_at?: string; // Make end_at optional for this component
  resource: {
    registration: {
      firstname?: string;
      lastname?: string;
      links: {
        show: string;
      };
    };
  };
}

const props = defineProps<Props>();
const { t } = useI18n();

// Reactive variables
const histories = ref<ExtendedHistory[]>([]);
const loading = ref(false);

// Method to convert history types to proper labels
const getHistoryTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    'Booking': t('Booking'),
    'ProductTest': t('Product test'),
    'Allotment': t('Allotment')
  };

  return typeLabels[type] || type;
};

// Method to get the appropriate icon for each history type
const getHistoryIcon = (type: string) => {
  const typeIcons = {
    'Booking': 'CalendarDays',
    'ProductTest': 'ArrowLeftRight',
    'Allotment': 'CalendarRange'
  } as const;

  return typeIcons[type] || 'CircleDashed';
};

const getHistories = async () => {
  loading.value = true;

  try {
    const historyModel = new HistoryModel();
    const where = {
      equipment_id: props.equipmentId,
      historiable_type: ['booking', 'allotment'],
    };

    const historyResult = await historyModel
      .where(where)
      .orderBy('-start_at')
      .get();

    histories.value = historyResult.data;
  } finally {
    loading.value = false;
  }
};

getHistories()
</script>
